// Service-type landing pages: /services/:slug
// Each page targets a specific commercial intent keyword.

export const SERVICES = [
  {
    slug: "dry-cleaning-gurugram",
    name: "Dry Cleaning",
    kw: "dry cleaning in Gurugram",
    title: "Dry Cleaning in Gurugram · Doorstep Pickup",
    metaDescription:
      "Professional dry cleaning in Gurugram with free pickup & delivery. Suits, silks, sarees, embroidered garments & designer wear — solvent-cleaned, hand-pressed, 48-hour turnaround. From ₹149 per piece.",
    h1: "Dry cleaning, done properly.",
    h1Accent: "Gurugram doorstep · 48-hour turnaround.",
    lede: "Suits, silks, sarees, gowns and everyday delicates — solvent-cleaned by hand, quality-checked, and pressed to a crisp finish before we deliver them folded to your door.",
    bookServiceId: "dry_cleaning",
    priceFrom: 149,
    priceUnit: "per piece",
    turnaround: "48 hours",
    benefits: [
      { title: "Hand-inspected before we clean", body: "Every garment is pre-treated for stains and checked for loose beading, sequin work or embroidery before it enters our solvent." },
      { title: "Perc-free, fabric-safe process", body: "We use hydrocarbon and silicone-based solvents so silks stay soft, sequins stay on, and colours stay vivid." },
      { title: "Pressed by hand", body: "Steam-ironed on finish tables, folded on tissue paper — the way your favourite tailor packs a new suit." },
    ],
    idealFor: ["Suits & blazers", "Silk sarees & lehengas", "Embroidered kurtas", "Silk & satin gowns", "Ties, scarves & shawls", "Winter coats & pashminas"],
    faqs: [
      { q: "How is dry cleaning different from washing?", a: "Dry cleaning uses a chemical solvent instead of water. Delicate fabrics like silk, wool, embroidered work and structured suits can shrink, bleed colour or lose their shape in water — dry cleaning removes soil and stains without ever wetting the fabric." },
      { q: "How long does dry cleaning take at RIZ N WASH?", a: "Standard turnaround is 48 hours from pickup. Rush service (24 hours) is available on request for a small surcharge — call us." },
      { q: "Can you remove tough stains like ink, oil or curry?", a: "Yes — mention the stain when we pick up so our team can pre-treat it. Older set-in stains are harder to remove; we'll be upfront if a mark can't be lifted before we clean." },
      { q: "Do you dry clean wedding lehengas and sherwanis?", a: "Yes. Heavy work garments go through our Premium Care line — hand-cleaned, laid flat, and returned on hangers with dust covers." },
    ],
    relatedServices: ["wash-and-fold-gurugram", "shoe-cleaning-gurugram", "curtain-carpet-cleaning-gurugram"],
    hero: "https://images.unsplash.com/photo-1549040634-41fbcd2eb623",
  },
  {
    slug: "shoe-cleaning-gurugram",
    name: "Shoe & Sneaker Cleaning",
    kw: "shoe cleaning in Gurugram",
    title: "Sneaker & Leather Shoe Cleaning, Gurugram",
    metaDescription:
      "Sneaker cleaning, leather shine and shoe restoration in Gurugram with free doorstep pickup. Deep-clean whites, restore soles, treat leather. From ₹399 per pair.",
    h1: "Bring your sneakers back to Day 1.",
    h1Accent: "Deep clean · sole revival · leather care.",
    lede: "Off-white midsoles that look yellow? Suede uppers gone dusty? A pair of Air Force 1s that need a fresh start? We deep-clean, deodorise and hand-finish every shoe like it's a museum piece.",
    bookServiceId: "premium_care",
    priceFrom: 399,
    priceUnit: "per pair",
    turnaround: "3–5 days",
    benefits: [
      { title: "Uppers, laces, insoles — all handled", body: "Uppers hand-brushed, laces washed separately, insoles deodorised and dried — even the tongue gets attention." },
      { title: "Sole revival that actually works", body: "Yellow midsoles brightened with a fabric-safe oxidising wash. Grippy soles return without bleach damage." },
      { title: "Leather nourishing, not just polish", body: "We condition dry leather with a natural cream base before hand-polishing — it flexes better and lasts longer." },
    ],
    idealFor: ["White sneakers (AF1, Stan Smith)", "Suede boots & Chelsea", "Running shoes & mesh trainers", "Formal leather shoes", "Kids' school shoes", "Handbags & wallets"],
    faqs: [
      { q: "How much does sneaker cleaning cost in Gurugram?", a: "Starting ₹399 per pair for standard sneaker cleaning. Deep-clean packages (with sole revival + leather conditioning) start at ₹599. Handbags priced by size." },
      { q: "Can you clean suede without damaging it?", a: "Yes — suede uses a specific dry-brush and eraser workflow, no water. We never scrub or soak suede." },
      { q: "How long does shoe cleaning take?", a: "3–5 days including drying time. Wet-clean uppers need to air-dry naturally to avoid warping." },
      { q: "Do you restore old leather bags too?", a: "Yes — leather bags follow the same conditioning workflow as leather shoes. Book under 'Premium Care'." },
    ],
    relatedServices: ["dry-cleaning-gurugram", "wash-and-fold-gurugram"],
    hero: "https://images.pexels.com/photos/12628400/pexels-photo-12628400.jpeg",
  },
  {
    slug: "wash-and-fold-gurugram",
    name: "Wash & Fold",
    kw: "wash and fold service Gurugram",
    title: "Wash & Fold Laundry Service, Gurugram",
    metaDescription:
      "Wash & fold laundry service in Gurugram — pickup, machine-wash, ironing, and folded delivery in 24 hours. From ₹19 per piece. Free doorstep pickup on orders above ₹299.",
    h1: "Wash. Iron. Fold. Repeat.",
    h1Accent: "₹19/piece · 24-hour turnaround.",
    lede: "Your weekly laundry, on a schedule. Colour-sorted, machine-washed at the right temperature, dried, hand-ironed and folded — back at your door before the next weekend.",
    bookServiceId: "wash_iron",
    priceFrom: 119,
    priceUnit: "per piece",
    turnaround: "24 hours",
    benefits: [
      { title: "Colour-sorted, never mixed", body: "Whites, colours and delicates are washed in separate loads — no bleeding, no faded blacks." },
      { title: "Temperature-controlled machines", body: "Cotton, synthetics and wool each get the correct cycle and water temp — the same care your washing manual asks for." },
      { title: "Hand-ironed, not just tumble-dried", body: "Shirts, kurtas and pants are pressed by hand on finish tables. Folded on tissue paper before packing." },
    ],
    idealFor: ["Weekly household laundry", "Bachelors & professionals", "Newborn / baby clothes (separate cycle)", "Cotton bedsheets & pillow covers", "Kids' school uniforms", "Gym & workout clothes"],
    faqs: [
      { q: "How does per-piece laundry pricing work?", a: "Each garment is counted at pickup. ₹19/piece covers wash + dry + ironing + folding. There's no surcharge for hangers, folding or standard packaging." },
      { q: "How is turnaround so fast?", a: "Pickups before 11 AM come back by the next evening. We run our machines through the day so weekend loads don't queue behind Monday's." },
      { q: "What if my clothes are damaged?", a: "Every load is quality-checked before delivery. In the rare case of damage we credit up to 5× the wash charge — no arguments." },
      { q: "Is there a minimum weight?", a: "Minimum order ₹149 (about 1.2 kg). Free doorstep pickup above ₹299." },
    ],
    relatedServices: ["dry-cleaning-gurugram", "bedsheet-duvet-cleaning-gurugram"],
    hero: "https://images.unsplash.com/photo-1605459437907-541c4e2c0ed1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMGNsZWFuJTIwZm9sZGVkJTIwd2hpdGUlMjBsaW5lbnxlbnwwfHx8fDE3ODMxNjEwODJ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "curtain-carpet-cleaning-gurugram",
    name: "Curtain & Carpet Cleaning",
    kw: "curtain and carpet cleaning Gurugram",
    title: "Curtain & Carpet Dry Cleaning, Gurugram",
    metaDescription:
      "Curtain and carpet cleaning in Gurugram at home. Deep-clean drapes, sofa upholstery, wool carpets and rugs — pickup, clean, deliver. From ₹40 per sq ft.",
    h1: "Curtains, carpets & upholstery.",
    h1Accent: "Cleaned properly. Delivered fresh.",
    lede: "Six months of Delhi NCR dust doesn't come off in a machine wash. Our home-textile line dry-cleans curtains, rugs and sofa fabric without shrinkage or colour loss — and delivers them back the same week.",
    bookServiceId: "premium_care",
    priceFrom: 40,
    priceUnit: "per sq ft",
    turnaround: "3–5 days",
    benefits: [
      { title: "Dust-vacuumed, then wet-cleaned", body: "Every curtain is dust-vacuumed before wet-cleaning so soil doesn't set into the fabric weave." },
      { title: "Wool carpets kept flat", body: "Handwoven wool and cotton rugs are laid flat to dry — no drum-drying, no shrinkage." },
      { title: "Blackout linings preserved", body: "We test detergent compatibility on blackout, thermal and interlined drapes before every clean." },
    ],
    idealFor: ["Living-room drapes", "Bedroom sheer curtains", "Wool & silk carpets", "Sofa upholstery (fabric)", "Dining chair covers", "Cushion covers & throws"],
    faqs: [
      { q: "How do you clean curtains without taking them down for weeks?", a: "We take down and rehang on the same visit for most standard rods. For heavy custom rods we send a two-person team. Cleaning happens off-site over 3–5 days." },
      { q: "Can you clean sofas without moving them?", a: "For fabric sofa upholstery we can do on-site steam cleaning at your home. Loose covers we take back to the studio." },
      { q: "Will colours fade?", a: "No — every curtain and rug is pre-tested for colourfastness on a hidden section before we clean the full piece." },
    ],
    relatedServices: ["bedsheet-duvet-cleaning-gurugram", "dry-cleaning-gurugram"],
    hero: "https://images.unsplash.com/photo-1489274495757-95c7c837b101?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxpcm9uaW5nJTIwY2xvdGhlcyUyMHByZXNzaW5nfGVufDB8fHx8MTc4MzE2MTA4Mnww&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "bedsheet-duvet-cleaning-gurugram",
    name: "Duvet & Bedsheet Cleaning",
    kw: "duvet cleaning Gurugram",
    title: "Duvet, Quilt & Bedsheet Cleaning, Gurugram",
    metaDescription:
      "Winter blanket, duvet, quilt and bedsheet cleaning in Gurugram with pickup & delivery. Down-safe wash, allergen-free finish, fluffed-up return. From ₹299 per duvet.",
    h1: "Fresh sheets. Fluffier duvets.",
    h1Accent: "Allergen-free · pickup & delivery.",
    lede: "Bed linen you sleep in for hours needs more than a rinse. We wash sheets at hygiene-safe temperatures, tumble-dry duvets to fluff up the fill, and return everything smelling like a hotel turndown.",
    bookServiceId: "wash_iron",
    priceFrom: 299,
    priceUnit: "per duvet",
    turnaround: "48 hours",
    benefits: [
      { title: "Hot-wash for dust mites", body: "Sheets and pillow covers washed at 60°C — kills dust-mite allergens without shrinking cotton." },
      { title: "Duvets tumble-dried, not folded", body: "Down and microfibre duvets are dried with drier balls to keep the fill lofty and even." },
      { title: "Ironed & tissue-folded", body: "Every sheet is ironed and folded on tissue so it feels crisp when you slip it on." },
    ],
    idealFor: ["Single & double bedsheets", "Duvets, quilts, comforters", "Pillow & bolster covers", "Cotton dohars & razais", "Baby-cot linen", "Guest-room turnover linen"],
    faqs: [
      { q: "How often should I clean my duvet?", a: "Cover-in-a-cover: wash the duvet cover weekly, the duvet itself 2–3 times a year (or right before/after winter)." },
      { q: "Will my duvet lose its fluff?", a: "No — we tumble-dry with drier balls specifically to redistribute the down/microfibre fill. It comes back fluffier than before." },
      { q: "Do you clean silk-filled razais?", a: "Yes, but they follow the Premium Care flow — hand-cleaned, laid flat to dry. Timeline is 4–5 days." },
    ],
    relatedServices: ["wash-and-fold-gurugram", "curtain-carpet-cleaning-gurugram"],
    hero: "https://images.unsplash.com/photo-1639690222869-1e608aa51f82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMGNsZWFuJTIwZm9sZGVkJTIwd2hpdGUlMjBsaW5lbnxlbnwwfHx8fDE3ODMxNjEwODJ8MA&ixlib=rb-4.1.0&q=85",
  },
];

export const SERVICE_BY_SLUG = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));

export function findService(slug) {
  if (!slug) return null;
  return SERVICE_BY_SLUG[String(slug).toLowerCase()] || null;
}
